import { useEffect } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L, { LatLngExpression} from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Marker } from 'react-leaflet'
import {useAppSelector} from "../../app/hooks";
import {selectCurrentDistrict} from "../../store/districtsReducer";

type CoordsInput = {
    lat?: number | string | null | undefined;
    lng?: number | string | null | undefined;
};

const toFiniteNumber = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') return null;
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const isValidLatLng = (lat: unknown, lng: unknown): lat is number => {
    const latNum = toFiniteNumber(lat);
    const lngNum = toFiniteNumber(lng);
    return (
        latNum !== null &&
        lngNum !== null &&
        latNum >= -90 &&
        latNum <= 90 &&
        lngNum >= -180 &&
        lngNum <= 180
    );
};

const RecenterMap = ({lat, lng}: {lat: number; lng: number}) => {
    const map = useMap();

    useEffect(() => {
        // `center` prop is only used on initial mount; this keeps the view in sync.
        map.setView([lat, lng], map.getZoom(), { animate: false });
    }, [lat, lng, map]);

    return null;
};

const MapForDistrictPoint = ({coordinates}: {coordinates: CoordsInput}) => {
    const currentDistrict = useAppSelector(selectCurrentDistrict)

    const districtLat = toFiniteNumber(currentDistrict?.latitude);
    const districtLng = toFiniteNumber(currentDistrict?.longitude);

    const propLat = toFiniteNumber(coordinates?.lat);
    const propLng = toFiniteNumber(coordinates?.lng);

    const hasValidDistrictCoords = isValidLatLng(districtLat, districtLng);
    const hasValidPropCoords = isValidLatLng(propLat, propLng);

    // If coordinates prop is valid and differs from district coords, use it
    const shouldUsePropCoords = hasValidPropCoords && (!hasValidDistrictCoords || propLat !== districtLat || propLng !== districtLng);

    if (!hasValidDistrictCoords && !hasValidPropCoords) return null;

    const centerLat = (shouldUsePropCoords ? propLat : districtLat) as number;
    const centerLng = (shouldUsePropCoords ? propLng : districtLng) as number;

    const iconPosition: LatLngExpression = { lat: centerLat, lng: centerLng }
    const iconImageType = currentDistrict?.organization.icon_format?.toLowerCase();
    
    const icon = L.icon({
        iconUrl: `data:image/${iconImageType === 'svg' ? 'svg+xml' : iconImageType};base64,${currentDistrict?.organization.icon}`,
        iconSize: [32, 32],
    })

    return (
        <MapContainer
            center={iconPosition}
            zoom={13}
            style={{ height: '275px', width: '100%', borderRadius: '12px' }}
        >
            <RecenterMap lat={centerLat} lng={centerLng} />
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
            />
            <Marker position={iconPosition} icon={icon} />
        </MapContainer>
    );
};

export default MapForDistrictPoint;


